import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X, ChevronDown, ChevronRight, Filter } from 'lucide-react';
import { Logo } from './Logo';
import { ContactModal } from './ContactModal';


type Product = { key: string; name: string; path: string; logo?: string };

const industries = [
  { key: 'banking', name: 'Banking / Financial Services', path: '/industries/banking-financial-services' },
  { key: 'construction', name: 'Construction', path: '/industries/construction' },
  { key: 'education', name: 'Education', path: '/industries/education' },
  { key: 'greenhydrogen', name: 'Green Hydrogen', path: '/industries/green-hydrogen' },
  { key: 'healthcare', name: 'Healthcare', path: '/industries/healthcare' },
  { key: 'manufacturing', name: 'Manufacturing', path: '/industries/manufacturing' },
  { key: 'oilgas', name: 'Oil & Gas', path: '/industries/oil-gas' },
  { key: 'realestate', name: 'Real Estate', path: '/industries/real-estate' },
  { key: 'retail', name: 'Retail / E-commerce', path: '/industries/retail-ecommerce' },
  { key: 'technology', name: 'Technology / IT Services', path: '/industries/technology-it-services' },
] as const;


type IndustryKey = typeof industries[number]['key'];

// Default fallback logo for products without specific logos
const DEFAULT_PRODUCT_LOGO = '/assets/Products/Logos/amasQIS.png';

const products = {
ai: [
  { key: 'cryptoTradingPlatform', name: 'Crypto Trading Platform', path: '/products/ai/cryptotradingplatform', logo: '' },
  { key: 'financePolicyCheck', name: 'Finance Policy Check', path: '/products/ai/financepolicycheck', logo: '' },
  { key: 'geneSeqer', name: 'GeneSeqer', path: '/products/ai/geneseqer', logo: '' },
  { key: 'greenH2', name: 'Green H2', path: '/products/greenh2', logo: '' },
  { key: 'lms', name: 'Learning Management', path: '/products/ai/LearningManagementSystem', logo: '' },
  { key: 'mvsxAi', name: 'MVS x AI', path: '/products/ai/mvsxAi', logo: '' },
  { key: 'panaceon', name: 'Panaceon', path: '/products/ai/Panaceon', logo: '/assets/Products/Logos/Panaceon.png' },
  { key: 'planfusion', name: 'Planfusion', path: '/products/ai/planfusion', logo: '' },
  // { key: 'predictiveMaintenance', name: 'Predictive Maintenance', path: '/products/ai/predictivemaintenance', logo: '' }, // hidden
  { key: 'realEstate', name: 'Real Estate', path: '/products/real-estate', logo: '' },
  { key: 'styleSync', name: 'Style Sync', path: '/products/style-sync', logo: '' },
  // { key: 'tawjihxAi', name: 'Tawjih x AI', path: '/products/ai/tawjihai', logo: '' }, // hidden
  { key: 'whispeRAGon', name: 'WhispeRAGon', path: '/products/ai/whisperagon', logo: '' },
] as Product[],

 it: [
  { key: 'manageRTC', name: 'ManageRTC', path: '/products/it/ManageRTC', logo: '/assets/Products/Logos/manageRTC.png' },
  { key: 'websites', name: 'Websites', path: '/products/it/Websites', logo: '' },
] as Product[],

iot: [
  { key: 'aiCooler', name: 'AI Cooler', path: '/products/ai/aicooler', logo: '' },
  { key: 'assetMgmt', name: 'Asset Management', path: '/products/ai/assetmanagement', logo: '' },
  { key: 'waterLevel', name: 'Water Level IoT', path: '/products/iot/water-level-iot', logo: '' },
] as Product[],

};

type CategoryKey = keyof typeof products;

const industryProductMap: Record<IndustryKey, Record<CategoryKey, string[]>> = {
  banking: {
    ai: ['financePolicyCheck','cryptoTradingPlatform'],
    it: [],
    iot: [],
  },
  construction: {
    ai: ['planfusion','whispeRAGon'],
    it: ['manageRTC'],
    iot: [],
  },
  education: {
    ai: ['lms','whispeRAGon'],
    it: [],
    iot: [],
  },
  greenhydrogen: {
    ai: ['greenH2'],
    it: [],
    iot: [],
  },
  healthcare: {
    ai: ['panaceon','geneSeqer'],
    it: [],
    iot: [],
  },
  manufacturing: {
    ai: ['mvsxAi','whispeRAGon'],
    it: ['manageRTC'],
    iot: ['assetMgmt','aiCooler','waterLevel'],
  },
  oilgas: {
    ai: ['mvsxAi'],
    it: [],
    iot: ['assetMgmt'],
  },
  realestate: {
    ai: ['realEstate'],
    it: [],
    iot: [],
  },
  retail: {
    ai: ['whispeRAGon','styleSync'],
    it: ['websites'],
    iot: [],
  },
  technology: {
    ai: [],
    it: ['manageRTC','websites'],
    iot: [],
  },
};



function useFilteredProducts(selected: IndustryKey | null) {
  if (!selected) return products;

  const keys = industryProductMap[selected];
  const pick = (bucket: Product[], allow: string[]) => bucket.filter(p => allow.includes(p.key));
  return {
    ai: pick(products.ai, keys.ai),
    it: pick(products.it, keys.it),
    iot: pick(products.iot, keys.iot),
  } as typeof products;
}

const ProductMenuItem: React.FC<{ to: string; children: React.ReactNode; logo?: string }> = ({ to, children, logo }) => (
  <Link
    to={to}
    className="flex items-center gap-3 px-3 py-2 text-sm rounded-lg hover:bg-primary/20 transition-colors text-muted-foreground hover:text-foreground group"
  >
    <div className="flex-shrink-0 w-8 h-8 rounded-md overflow-hidden bg-white/5 ring-1 ring-white/10 group-hover:ring-primary/50 transition-all">
      <img 
        src={logo || DEFAULT_PRODUCT_LOGO} 
        alt=""
        className="w-full h-full object-cover"
        onError={(e) => {
          e.currentTarget.src = DEFAULT_PRODUCT_LOGO;
        }}
      />
    </div>
    <span className="flex-1">{children}</span>
  </Link>
);

const Column: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div className="min-w-0">
    <div className="sticky top-0 z-10 px-3 py-2 bg-white/5 text-primary text-xs font-semibold tracking-wide uppercase rounded-lg mb-2">
      {title}
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

type NavItem = {
  name: string;
  path: string;
  dropdown?: Array<{ name: string; path: string }>;
  megaMenuType?: 'industryFilter';
};

export const Navigation: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [expandedMobile, setExpandedMobile] = useState<string | null>(null);
  const [shouldUseBurger, setShouldUseBurger] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [selectedIndustry, setSelectedIndustry] = useState<IndustryKey | null>(null);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [clickedDropdown, setClickedDropdown] = useState<string | null>(null);

  const navRef = useRef<HTMLDivElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    const checkNavOverflow = () => {
      if (navRef.current) {
        const nav = navRef.current;
        const containerWidth = nav.offsetWidth;
        const contentWidth = nav.scrollWidth;
        setShouldUseBurger(contentWidth > containerWidth || window.innerWidth < 1241);
      } else {
        setShouldUseBurger(window.innerWidth < 1241);
      }
    };

    // Close clicked dropdown when clicking outside
    const handleClickOutside = (event: MouseEvent) => {
      if (
        megaMenuRef.current && 
        !megaMenuRef.current.contains(event.target as Node) &&
        navRef.current &&
        !navRef.current.contains(event.target as Node)
      ) {
        setClickedDropdown(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', checkNavOverflow);
    document.addEventListener('mousedown', handleClickOutside);
    checkNavOverflow();

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', checkNavOverflow);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
    setExpandedMobile(null);
    setSelectedIndustry(null);
    setShowMobileFilter(false);
    setClickedDropdown(null);
  }, [location]);

  // Reset selected industry when mega menu closes
  useEffect(() => {
    const isProductsMenuVisible = activeDropdown === 'Products' || clickedDropdown === 'Products' || expandedMobile === 'Products';
    if (!isProductsMenuVisible) {
      setSelectedIndustry(null);
      setShowMobileFilter(false);
    }
  }, [activeDropdown, clickedDropdown, expandedMobile]);

  const navItems: NavItem[] = [
    { name: 'Home', path: '/' },
    {
      name: 'About Us',
      path: '/about',
      dropdown: [
        { name: 'Company', path: '/about' },
        { name: 'Solutions', path: '/solutions' },
        { name: 'Careers', path: '/careers' },
      ],
    },
    {
      name: 'Products',
      path: '#',
      megaMenuType: 'industryFilter',
    },
    {
      name: 'Resources',
      path: '/resources',
      dropdown: [
        { name: 'Case Studies', path: '/case-studies' },
      ],
    },
    { name: 'Contact Us', path: '/contact' },
  ];

  const filtered = useFilteredProducts(selectedIndustry);

  // Handle click to toggle mega menu
  const handleMenuClick = (itemName: string, event: React.MouseEvent) => {
    // Only handle clicks for items with dropdowns or mega menus
    const item = navItems.find(i => i.name === itemName);
    if (item?.dropdown || item?.megaMenuType) {
      event.preventDefault();
      setClickedDropdown(clickedDropdown === itemName ? null : itemName);
    }
  };

  // Determine if dropdown should be shown (either hovered or clicked)
  const isDropdownVisible = (itemName: string) => {
    return activeDropdown === itemName || clickedDropdown === itemName;
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 w-full z-[1000] transition-all duration-300 ${
        scrolled 
          ? 'glass-strong py-3 shadow-lg shadow-black/50 border-b border-white/10' 
          : 'backdrop-blur-md bg-background/50 py-4 border-b border-white/5'
      }`}
    >
      <div className="max-w-[100%] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div className="relative z-50 flex-shrink-0 px-2">
            <Logo variant="header" size="md" />
          </div>

          {!shouldUseBurger && (
            <div ref={navRef} className="flex items-center space-x-6 xl:space-x-8 relative z-40">
              {navItems.map((item) => (
                <div
                  key={item.name}
                  className={item.megaMenuType ? '' : 'relative'}
                  onMouseEnter={() => setActiveDropdown(item.name)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >

                  <Link
                    to={item.path}
                    onClick={(e) => handleMenuClick(item.name, e)}
                    className={`flex items-center gap-1 transition-all duration-300 hover:text-primary whitespace-nowrap ${location.pathname === item.path ? 'text-primary' : ''
                      }`}
                  >
                    {item.name}
                    {(item.dropdown || item.megaMenuType) && (
                      <motion.div
                        animate={{ rotate: isDropdownVisible(item.name) ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    )}
                  </Link>

                  <AnimatePresence>
                    {item.dropdown && isDropdownVisible(item.name) && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute top-full mt-2 w-72 dropdown-bg rounded-lg p-2 shadow-lg left-0 xl:left-auto xl:right-0 nav-dropdown"
                        style={{ zIndex: 999 }}
                      >
                        <div className="max-h-96 overflow-y-auto scrollbar-hide">
                          {item.dropdown.map((dd) => (
                            <Link
                              key={dd.name}
                              to={dd.path}
                              className="block px-4 py-2 text-sm hover:bg-primary/20 rounded transition-colors duration-200"
                            >
                              {dd.name}
                            </Link>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {item.megaMenuType === 'industryFilter' && isDropdownVisible(item.name) && (
                      <motion.div
                        ref={megaMenuRef}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="fixed left-0 right-0 top-[4.5rem] mx-auto w-[min(95vw,1200px)] rounded-xl shadow-2xl nav-dropdown"
                        style={{ maxWidth: 'calc(100vw - 2rem)', zIndex: 999 }}
                      >

                        <div className="dropdown-bg rounded-xl p-6 ring-1 ring-white/10 shadow-2xl">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-[280px_repeat(auto-fit,minmax(200px,1fr))] gap-6 max-h-[80vh] overflow-y-auto scrollbar-hide">
                            {/* Industries Column - Highlighted */}
                            <div className="min-w-0 lg:col-span-1 relative">
                              {/* Background highlight for entire column */}
                              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-accent/5 to-secondary/5 rounded-lg border border-primary/20"></div>
                              
                              <div className="relative max-h-[75vh] overflow-y-auto scrollbar-hide">
                                {/* Enhanced Header */}
                                <div className="sticky top-0 z-10 px-4 py-3 bg-gradient-to-r from-primary/15 to-accent/15 text-primary text-xs font-semibold tracking-wide uppercase rounded-lg mb-3 border border-primary/30 shadow-lg backdrop-blur-md">
                                  <div className="flex items-center gap-2">
                                    <Filter className="w-4 h-4" />
                                    <span>Industry</span>
                                  </div>
                                </div>
                                
                                {/* Industries List */}
                                <div className="px-2 pb-2">
                                  <ul className="space-y-1.5">
                                    {industries.map((ind) => {
                                      const selected = selectedIndustry === ind.key;
                                      return (
                                        <li key={ind.key}>
                                          <button
                                            type="button"
                                            onClick={() => setSelectedIndustry(selected ? null : ind.key)}
                                            className={`w-full text-left px-3 py-2.5 text-sm rounded-lg transition-all duration-200 ${selected
                                              ? 'bg-gradient-to-r from-primary/25 to-accent/25 ring-2 ring-primary/50 text-foreground shadow-md shadow-primary/20'
                                              : 'hover:bg-white/10 text-muted-foreground hover:text-foreground hover:ring-1 hover:ring-white/20'
                                            }`}
                                          >
                                            <div className="flex items-center justify-between">
                                              <span>{ind.name}</span>
                                              {selected && (
                                                <ChevronRight className="w-4 h-4 text-primary" />
                                              )}
                                            </div>
                                          </button>
                                          {selected && (
                                            <motion.div 
                                              initial={{ opacity: 0, height: 0 }}
                                              animate={{ opacity: 1, height: 'auto' }}
                                              exit={{ opacity: 0, height: 0 }}
                                              className="px-3 pt-1.5 pb-2"
                                            >
                                              <Link
                                                to={ind.path}
                                                className="inline-flex items-center gap-1 text-xs rounded-md px-3 py-1.5 bg-primary text-primary-foreground hover:bg-primary/85 transition-colors shadow-sm"
                                              >
                                                View Industry Details
                                                <ChevronRight className="w-3 h-3" />
                                              </Link>
                                            </motion.div>
                                          )}
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Only show columns with products */}
                            {filtered.ai.length > 0 && (
                              <Column title="AI Products">
                                {filtered.ai.map((p) => (
                                  <ProductMenuItem key={p.key} to={p.path} logo={p.logo}>
                                    {p.name}
                                  </ProductMenuItem>
                                ))}
                              </Column>
                            )}

                            {filtered.it.length > 0 && (
                              <Column title="IT Products">
                                {filtered.it.map((p) => (
                                  <ProductMenuItem key={p.key} to={p.path} logo={p.logo}>
                                    {p.name}
                                  </ProductMenuItem>
                                ))}
                              </Column>
                            )}

                            {filtered.iot.length > 0 && (
                              <Column title="IoT Products">
                                {filtered.iot.map((p) => (
                                  <ProductMenuItem key={p.key} to={p.path} logo={p.logo}>
                                    {p.name}
                                  </ProductMenuItem>
                                ))}
                              </Column>
                            )}


                            {/* Show message if no products match */}
                            {filtered.ai.length === 0 && 
                             filtered.it.length === 0 && 
                             filtered.iot.length === 0 && (
                              <div className="col-span-4 flex flex-col items-center justify-center py-12">
                                <p className="text-muted-foreground mb-4">No products available for this industry.</p>
                                <button
                                  onClick={() => setSelectedIndustry(null)}
                                  className="text-sm text-primary hover:text-primary/80 transition-colors px-4 py-2 bg-primary/10 rounded-lg hover:bg-primary/20"
                                >
                                  View All Products
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}

              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <button
                  onClick={() => setIsContactModalOpen(true)}
                  className="bg-primary text-primary-foreground px-6 py-2 rounded-lg hover:bg-primary/80 transition-all duration-300 neon-glow whitespace-nowrap"
                >
                  Get Started
                </button>
              </motion.div>
            </div>
          )}

          {shouldUseBurger && (
            <motion.button
              whileTap={{ scale: 0.95 }}
              className="glass p-2 rounded-lg relative z-40"
              onClick={() => setIsOpen(!isOpen)}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={isOpen ? 'close' : 'menu'}
                  initial={{ rotate: 0 }}
                  animate={{ rotate: 0 }}
                  exit={{ rotate: 90 }}
                  transition={{ duration: 0.2 }}
                >
                  {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </motion.div>
              </AnimatePresence>
            </motion.button>
          )}
        </div>

        <AnimatePresence>
          {isOpen && shouldUseBurger && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mt-4 dropdown-bg rounded-lg overflow-hidden relative nav-dropdown"
              style={{ zIndex: 999 }}
            >
              <div className="max-h-[70vh] overflow-y-auto scrollbar-hide p-4">
                {navItems.map((item) => (
                  <div key={item.name} className="border-b border-white/10 last:border-b-0">
                    {(item.dropdown || item.megaMenuType) ? (
                      <button
                        onClick={() => setExpandedMobile(expandedMobile === item.name ? null : item.name)}
                        className={`w-full flex items-center justify-between py-3 text-left transition-colors duration-200 hover:text-primary ${location.pathname === item.path ? 'text-primary' : ''
                          }`}
                      >
                        <span className="flex-1 text-lg">{item.name}</span>
                        <motion.div
                          animate={{ rotate: expandedMobile === item.name ? 90 : 0 }}
                          transition={{ duration: 0.2 }}
                        >
                          <ChevronRight className="w-5 h-5" />
                        </motion.div>
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className={`flex items-center justify-between py-3 text-lg transition-colors duration-200 hover:text-primary ${location.pathname === item.path ? 'text-primary' : ''
                          }`}
                      >
                        {item.name}
                      </Link>
                    )}

                    <AnimatePresence>
                      {item.dropdown && expandedMobile === item.name && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="pb-3"
                        >
                          <div className="ml-4 space-y-2">
                            {item.dropdown.map((dd) => (
                              <Link
                                key={dd.name}
                                to={dd.path}
                                className="block text-sm text-muted-foreground hover:text-primary transition-colors duration-200 py-1"
                              >
                                {dd.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {item.megaMenuType === 'industryFilter' && expandedMobile === item.name && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="pb-3"
                        >
                          <div className="ml-1 space-y-3">
                            {/* Filter Header with Button */}
                            <div className="flex items-center justify-between px-3 py-2 bg-white/5 rounded-lg">
                              <div className="flex items-center gap-2">
                                <Filter className="w-4 h-4 text-primary" />
                                <span className="text-sm font-semibold text-foreground">
                                  {selectedIndustry 
                                    ? industries.find(i => i.key === selectedIndustry)?.name 
                                    : 'All Products'}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => setShowMobileFilter(!showMobileFilter)}
                                className="px-3 py-1.5 text-xs bg-primary/20 hover:bg-primary/30 text-primary rounded-lg transition-colors"
                              >
                                {showMobileFilter ? 'Close' : 'Filter'}
                              </button>
                            </div>

                            {/* Industry Filter Dropdown */}
                            <AnimatePresence>
                              {showMobileFilter && (
                                <motion.div
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="overflow-hidden"
                                >
                                  <div className="glass-strong rounded-lg p-3 space-y-1 max-h-[50vh] overflow-y-auto scrollbar-hide">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSelectedIndustry(null);
                                        setShowMobileFilter(false);
                                      }}
                                      className={`w-full text-left px-3 py-2.5 text-sm rounded-lg transition-colors ${!selectedIndustry
                                        ? 'bg-primary/20 ring-1 ring-primary/30 text-foreground font-medium'
                                        : 'hover:bg-white/5 text-muted-foreground hover:text-foreground'
                                        }`}
                                    >
                                      All Industries
                                    </button>
                                    {industries.map((ind) => {
                                      const selected = selectedIndustry === ind.key;
                                      return (
                                        <div key={ind.key} className="space-y-1">
                                          <button
                                            type="button"
                                            onClick={() => {
                                              setSelectedIndustry(selected ? null : ind.key);
                                              setShowMobileFilter(false);
                                            }}
                                            className={`w-full text-left px-3 py-2.5 text-sm rounded-lg transition-colors ${selected
                                              ? 'bg-primary/20 ring-1 ring-primary/30 text-foreground font-medium'
                                              : 'hover:bg-white/5 text-muted-foreground hover:text-foreground'
                                              }`}
                                          >
                                            {ind.name}
                                          </button>
                                          {selected && (
                                            <div className="ml-3 mb-1">
                                              <Link
                                                to={ind.path}
                                                className="text-xs inline-flex items-center rounded-lg px-3 py-1.5 bg-primary text-primary-foreground hover:bg-primary/85 transition-colors"
                                              >
                                                View Industry Page
                                              </Link>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>

                            {/* Products List - Only show categories with products */}
                            <div className="space-y-3">
                              {/* AI Products */}
                              {filtered.ai.length > 0 && (
                                <div>
                                  <div className="px-3 py-2 bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase rounded-lg mb-2">
                                    AI Products ({filtered.ai.length})
                                  </div>
                                  <div className="space-y-1">
                                    {filtered.ai.map((p) => (
                                      <ProductMenuItem key={p.key} to={p.path} logo={p.logo}>
                                        {p.name}
                                      </ProductMenuItem>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* IT Products */}
                              {filtered.it.length > 0 && (
                                <div>
                                  <div className="px-3 py-2 bg-secondary/10 text-secondary text-xs font-semibold tracking-wide uppercase rounded-lg mb-2">
                                    IT Products ({filtered.it.length})
                                  </div>
                                  <div className="space-y-1">
                                    {filtered.it.map((p) => (
                                      <ProductMenuItem key={p.key} to={p.path} logo={p.logo}>
                                        {p.name}
                                      </ProductMenuItem>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* IoT Products */}
                              {filtered.iot.length > 0 && (
                                <div>
                                  <div className="px-3 py-2 bg-accent/10 text-accent text-xs font-semibold tracking-wide uppercase rounded-lg mb-2">
                                    IoT Products ({filtered.iot.length})
                                  </div>
                                  <div className="space-y-1">
                                    {filtered.iot.map((p) => (
                                      <ProductMenuItem key={p.key} to={p.path} logo={p.logo}>
                                        {p.name}
                                      </ProductMenuItem>
                                    ))}
                                  </div>
                                </div>
                              )}


                              {/* No products message */}
                              {filtered.ai.length === 0 && 
                               filtered.it.length === 0 && 
                               filtered.iot.length === 0 && (
                                <div className="text-center py-8">
                                  <p className="text-sm text-muted-foreground mb-3">
                                    No products available for this industry.
                                  </p>
                                  <button
                                    onClick={() => {
                                      setSelectedIndustry(null);
                                    }}
                                    className="text-xs text-primary hover:text-primary/80 transition-colors"
                                  >
                                    View All Products
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}

                <div className="pt-4 mt-4 border-t border-white/10">
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className="block w-full text-center bg-primary text-primary-foreground py-3 rounded-lg hover:bg-primary/80 transition-colors duration-300"
                  >
                    Get Started
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ContactModal isOpen={isContactModalOpen} onClose={() => setIsContactModalOpen(false)} />
    </motion.nav>
  );
};
